import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { type ChatbotRow, createChatbot, updateChatbot } from './api';

interface Props {
  chatbot?: ChatbotRow;
  onClose: () => void;
}

/**
 * Modal de criar/renomear chatbot. Após criar, redireciona pro editor.
 * No modo edição (chatbot != null) só renomeia (alterações de fluxo são no editor).
 */
export function ChatbotFormModal({ chatbot, onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEdit = !!chatbot;

  const [name, setName] = useState(chatbot?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  // Vínculo a número WhatsApp NÃO é mais aqui — o canal define qual chatbot
  // usa via configurações de canal (Pencil em /canais → default_chatbot_id).
  // 1 chatbot pode ser usado por N canais.
  const createMut = useMutation({
    mutationFn: () => createChatbot({ name: name.trim() }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['chatbots'] });
      onClose();
      // biome-ignore lint/suspicious/noExplicitAny: dynamic route params
      void navigate({ to: '/chatbot/$id/editor' as any, params: { id: String(res.id) } as any });
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  const updateMut = useMutation({
    mutationFn: () => updateChatbot(chatbot!.id, { name: name.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chatbots'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  const submit = (): void => {
    setError(null);
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (isEdit) updateMut.mutate();
    else createMut.mutate();
  };

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md bg-n-0 shadow-xl">
        <header className="flex items-center justify-between border-n-200 border-b px-6 py-4">
          <h2 className="font-bold text-[16px] text-n-900">
            {isEdit ? 'Editar chatbot' : 'Novo chatbot'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-n-500 hover:text-n-700"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </header>

        <div className="space-y-4 px-6 py-5">
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-n-700">Nome</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Triagem inicial"
              maxLength={120}
            />
          </label>

          {error && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-n-200 border-t bg-n-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-n-200 bg-n-0 px-3 py-2 text-[13px] font-semibold text-n-700 hover:bg-n-50"
          >
            Cancelar
          </button>
          <Button onClick={submit} disabled={isPending}>
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Salvar' : 'Criar e abrir editor'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
